import {useContext} from 'react';
import {AppContext} from '../state/App/AppContext.jsx';
import {defaultHtmlFormatValues, useNeuroMessages} from '../components/MessageContext';
import {formatNumberQuestionValues} from '../utils/helpers/questionHelper';

/**
 * @typedef AnswersHookValues
 * @property {Object} answers - list of saved answers in the state
 * @property {function} getAnswer - returns answer object if present, else null
 * @property {function} hasAnswer - returns true if key exists in locally saved answers list
 * @property {function} parseAnswerValue - returns true if key exists in locally saved answers list
 *
 * @return {AnswersHookValues}
 */
const useAnswers = () => {
  const messageContext = useNeuroMessages();
  const stateAnswers = useContext(AppContext).answers;
  const getAnswer = key => Object.prototype.hasOwnProperty.call( stateAnswers, key ) ? stateAnswers[key] : null;
  const hasAnswer = key => Object.prototype.hasOwnProperty.call( stateAnswers, key );

  const parseAnswerValue = ({answer, useDetailsMessage}) => {
    const { appkey, value, type = '', view = {} } = answer;
    const attributes = view.attributes || {};
    const questionType = view.type ? view.type.toLowerCase() : '';
    const dataType = (attributes.dataType || type).toLowerCase();
    const id =
      useDetailsMessage && messageContext.hasMessage(`details.${appkey}.${value}`)
        ? `details.${appkey}.${value}`
        : `${appkey}.${value}`;
    let result = '';

    switch (dataType) {
      case 'number':
        result = formatNumberQuestionValues(attributes.format, value);
        break;
      case 'boolean':
        result = messageContext.formatIntlMessage({ id });
        break;
      default:
        result = _formatTextAnswerValues({ appkey, id, questionType, useDetailsMessage, value });
    }
    return result;
  };

  return {
    answers: stateAnswers,
    getAnswer,
    hasAnswer,
    parseAnswerValue
  };
};

const _formatTextAnswerValues = ({ appkey, id, questionType, useDetailsMessage, value }) => {
  const messageContext = useNeuroMessages();
  if (questionType === 'textinput') {
    return Array.isArray(value) ? value[0] : value;
  } else if (Array.isArray(value)) {
    const answerTextMap = value.reduce((agg, v) => {
      const id = useDetailsMessage && messageContext.hasMessage(`details.${appkey}.${v}`)
        ? `details.${appkey}.${v}`
        : `${appkey}.${v}`;
      agg = agg.concat( messageContext.formatIntlMessage({ id }, { ...defaultHtmlFormatValues }), ', ' );
      return agg;
    }, []);
    return answerTextMap.splice(0, answerTextMap.length - 1);
  } else {
    return value ? messageContext.formatIntlMessage({ id }, { ...defaultHtmlFormatValues }) : null;
  }
};


export {
  useAnswers
};
